import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useSite } from "../context/SiteContext";

const SLIDES = [
    {
        eyebrow: "Artist Development Platform",
        title: ["Build your", "artist career."],
        sub: "Not just your next song.",
        copy: "Music production, visuals, branding, marketing and distribution. Everything independent artists need to create, release and grow.",
        primary: { label: "Start Your Artist Journey", to: "/signup" },
        secondary: { label: "Explore Services", to: "/services" },
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    },
    {
        eyebrow: "Creative Studio",
        title: ["Your idea.", "Our craft."],
        sub: "Production · Mixing · Visuals · Design",
        copy: "Music production, mixing, music videos, album art and creative design, crafted by a team that treats your record like its own.",
        primary: { label: "Explore Our Work", to: "/portfolio" },
        secondary: { label: "View Services", to: "/services" },
        image: "https://images.pexels.com/photos/31949770/pexels-photo-31949770.jpeg?auto=compress&w=2000",
    },
    {
        eyebrow: "Grow Your Audience",
        title: ["Create something", "people remember."],
        sub: "Branding · Marketing · Distribution",
        copy: "Artist branding, marketing, distribution and development. Turn moments into a career people never forget.",
        primary: { label: "Build Your Artist Career", to: "/signup" },
        secondary: { label: "See the Work", to: "/portfolio" },
        image: "https://images.pexels.com/photos/15208427/pexels-photo-15208427.jpeg?auto=compress&w=2000",
    },
];

export default function HeroCarousel() {
    const [index, setIndex] = useState(0);
    const timer = useRef(null);
    const { openLead } = useSite();
    const go = useCallback((n) => setIndex((n + SLIDES.length) % SLIDES.length), []);
    const next = useCallback(() => go(index + 1), [index, go]);
    const prev = useCallback(() => go(index - 1), [index, go]);

    useEffect(() => {
        timer.current = setTimeout(next, 7000);
        return () => clearTimeout(timer.current);
    }, [index, next]);

    const slide = SLIDES[index];

    return (
        <section className="relative bg-espresso text-white overflow-hidden h-[calc(100vh-68px)] min-h-[560px]" data-testid="hero-carousel">
            {/* Full-bleed image */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={index}
                    className="absolute inset-0"
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                >
                    <img src={slide.image} alt="MUVIARTS" className="h-full w-full object-cover" loading="eager" fetchPriority="high" decoding="async" />
                    <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/50 to-espresso/20" />
                    <div className="absolute inset-0 bg-gradient-to-r from-espresso/85 via-espresso/30 to-transparent" />
                </motion.div>
            </AnimatePresence>

            {/* Text */}
            <div className="relative z-20 mx-container h-full flex flex-col justify-center">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={index}
                        className="max-w-2xl text-center lg:text-left mx-auto lg:mx-0"
                        initial={{ opacity: 0, y: 26 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <p className="overline !text-brand-gold mb-5 flex items-center justify-center lg:justify-start gap-3">
                            <span className="h-px w-8 bg-brand-gold inline-block" /> {slide.eyebrow}
                        </p>
                        <h1 className="font-display font-semibold leading-[0.98] text-5xl sm:text-7xl lg:text-[5.5rem] tracking-tight">
                            {slide.title.map((line, i) => (
                                <span key={i} className="block">{i === 1 ? <span className="italic text-brand-gold">{line}</span> : line}</span>
                            ))}
                        </h1>
                        <p className="mt-5 font-display italic text-xl sm:text-2xl text-white/80">{slide.sub}</p>
                        <p className="mt-6 max-w-lg mx-auto lg:mx-0 text-white/70 leading-relaxed">{slide.copy}</p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-3 items-center justify-center lg:justify-start lg:items-start">
                            <button onClick={() => openLead?.("General")} data-testid="hero-primary-cta" className="btn-primary">
                                Start Your Project <ArrowRight className="h-4 w-4" />
                            </button>
                            <Link to={slide.secondary.to} className="btn-ghost-light !px-2 uppercase text-sm font-semibold tracking-wide" data-testid="hero-secondary-cta">
                                {slide.secondary.label} <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Controls */}
            <div className="absolute inset-x-0 bottom-7 lg:bottom-10 z-30">
                <div className="mx-container flex items-center justify-between">
                    <div className="flex items-center gap-5" data-testid="hero-indicators">
                        {SLIDES.map((_, i) => (
                            <button key={i} onClick={() => go(i)} data-testid={`hero-dot-${i}`} className="group flex items-center gap-2" aria-label={`Slide ${i + 1}`}>
                                <span className={`font-display text-sm transition-colors ${i === index ? "text-brand-gold" : "text-white/40"}`}>0{i + 1}</span>
                                <span className={`h-px transition-all duration-300 ${i === index ? "w-10 bg-brand-gold" : "w-5 bg-white/25"}`} />
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={prev} className="h-11 w-11 rounded-full border border-white/25 grid place-items-center hover:border-brand-gold hover:text-brand-gold transition-colors" data-testid="hero-prev" aria-label="Previous"><ArrowLeft className="h-4 w-4" /></button>
                        <button onClick={next} className="h-11 w-11 rounded-full border border-white/25 grid place-items-center hover:border-brand-gold hover:text-brand-gold transition-colors" data-testid="hero-next" aria-label="Next"><ArrowRight className="h-4 w-4" /></button>
                    </div>
                </div>
            </div>
        </section>
    );
}
